// ── Schwab thinkorswim CSV Account Statement Parser ──────────────────────────

const MONTH_MAP = {
  JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
  JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
}

/**
 * Parse a thinkorswim CSV account statement.
 * Returns { accountName, dateRange, executions, summary }
 */
export function parseThinkorswimCSV(csvText) {
  const lines = csvText.split(/\r?\n/)

  // ── Extract account name from line 1 ──────────────────────────────────────
  // "Account Statement for BA Brokerage since 01/01/25 through 11/25/25"
  const headerMatch = lines[0]?.match(/Account Statement for (.+?) since (.+?) through (.+)/)
  const accountName = headerMatch ? headerMatch[1].trim() : null
  const dateRangeFrom = headerMatch ? headerMatch[2].trim() : null
  const dateRangeTo = headerMatch ? headerMatch[3].trim() : null

  // ── Find section start lines ──────────────────────────────────────────────
  let tradeHistoryStart = -1
  let orderHistoryStart = -1

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim()
    if (trimmed === 'Account Trade History') tradeHistoryStart = i
    if (trimmed === 'Account Order History') orderHistoryStart = i
  }

  // ── Parse Account Trade History ───────────────────────────────────────────
  const executions = []

  if (tradeHistoryStart >= 0) {
    // Skip section header line, next line is column header
    let i = tradeHistoryStart + 2 // skip header row too
    let rowIndex = 0

    while (i < lines.length) {
      const line = lines[i]

      // End of section: blank line or next section header
      if (line.trim() === '' || /^[A-Z][a-z]/.test(line.trim()) && !line.trim().startsWith(',')) {
        // Check if it's actually a section header (not a data row)
        if (line.trim() === '' || (line.indexOf(',') === -1 && line.trim().length > 0)) {
          break
        }
      }

      const cols = parseCSVRow(line)

      // Skip empty rows or rows without exec time
      const execTime = (cols[1] || '').trim()
      if (!execTime) {
        // Might be a COMBO/VERTICAL second leg — check if previous execution exists
        if (cols[3]?.trim() && executions.length > 0) {
          // This is a second leg of a multi-leg trade
          const parentExec = executions[executions.length - 1]
          const legExec = parseTradeRow(cols, parentExec.raw.exec_time, rowIndex)
          if (legExec) {
            legExec.isComboLeg = true
            legExec.comboParentIndex = executions.length - 1
            executions.push(legExec)
            rowIndex++
          }
        }
        i++
        continue
      }

      const exec = parseTradeRow(cols, null, rowIndex)
      if (exec) {
        executions.push(exec)
        rowIndex++
      }

      i++
    }
  }

  // ── Build summary ─────────────────────────────────────────────────────────
  const equityCount = executions.filter(e => e.assetType === 'equity').length
  const etfCount = executions.filter(e => e.assetType === 'etf').length
  const optionCount = executions.filter(e => e.assetType === 'option').length
  const futureCount = executions.filter(e => e.assetType === 'future').length
  const stpCount = executions.filter(e => e.orderType === 'STP').length

  // Date range from actual data
  let minDate = null, maxDate = null
  for (const e of executions) {
    if (!minDate || e.tradeDate < minDate) minDate = e.tradeDate
    if (!maxDate || e.tradeDate > maxDate) maxDate = e.tradeDate
  }

  return {
    accountName,
    dateRange: { from: dateRangeFrom, to: dateRangeTo },
    executions,
    summary: {
      total: executions.length,
      equities: equityCount,
      etfs: etfCount,
      options: optionCount,
      futures: futureCount,
      stopTriggered: stpCount,
      dateRange: minDate && maxDate
        ? { from: minDate, to: maxDate }
        : null,
    },
  }
}

// ── Parse a single CSV row into fields, handling quoted commas ───────────────

function parseCSVRow(line) {
  const result = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      result.push(current)
      current = ''
    } else {
      current += ch
    }
  }
  result.push(current)
  return result
}

// ── Parse a trade history row ───────────────────────────────────────────────

function parseTradeRow(cols, parentExecTime, rowIndex) {
  const rawExecTime = (cols[1] || '').trim() || parentExecTime
  if (!rawExecTime) return null

  const spread = (cols[2] || '').trim()
  const side = (cols[3] || '').trim().toUpperCase()
  const rawQty = (cols[4] || '').trim()
  const posEffect = (cols[5] || '').trim().toUpperCase()
  const rawSymbol = (cols[6] || '').trim()
  const exp = (cols[7] || '').trim()
  const strike = (cols[8] || '').trim()
  const type = (cols[9] || '').trim().toUpperCase()
  const price = parseFloat((cols[10] || '').trim()) || 0
  const netPrice = parseFloat((cols[11] || '').trim()) || 0
  const orderType = (cols[12] || '').trim().toUpperCase()

  if (!side || !rawSymbol) return null

  // Parse quantity (remove +/-)
  const qty = Math.abs(parseInt(rawQty, 10)) || 0
  if (qty === 0) return null

  // Parse exec time: "11/25/25 09:03:14" → ISO
  const tradeDate = parseTosDateTime(rawExecTime)
  if (!tradeDate) return null

  // Determine asset type
  const assetType = mapAssetType(type, rawSymbol)

  // Build normalized symbol
  const normalizedSymbol = buildNormalizedSymbol(rawSymbol, exp, strike, type, assetType)

  // Map position effect
  const positionEffect = posEffect.includes('OPEN') ? 'OPENING' : 'CLOSING'

  // Determine side as buy/sell for DB
  const dbSide = side === 'BUY' ? 'buy' : 'sell'

  return {
    rowIndex,
    tradeDate,
    spread,
    side: dbSide,
    sideRaw: side,
    qty,
    posEffect,
    positionEffect,
    symbol: normalizedSymbol,
    rawSymbol,
    exp,
    strike,
    type,
    assetType,
    price,
    netPrice,
    orderType,
    raw: {
      exec_time: rawExecTime,
      spread,
      side,
      qty: rawQty,
      pos_effect: posEffect,
      symbol: rawSymbol,
      exp,
      strike,
      type,
      price,
      net_price: netPrice,
      order_type: orderType,
      import_source: 'csv_thinkorswim',
    },
  }
}

// ── Parse thinkorswim datetime: "11/25/25 09:03:14" → ISO string ────────────

function parseTosDateTime(str) {
  const match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/)
  if (!match) return null

  const [, mm, dd, yy, hh, min, ss] = match
  const year = parseInt(yy, 10) + 2000
  const month = mm.padStart(2, '0')
  const day = dd.padStart(2, '0')

  // Return ISO string (assume US Eastern for thinkorswim)
  return `${year}-${month}-${day}T${hh}:${min}:${ss}`
}

// ── Map CSV Type field to database asset_type ───────────────────────────────

function mapAssetType(type, symbol) {
  if (type === 'STOCK') return 'equity'
  if (type === 'ETF') return 'etf'
  if (type === 'CALL' || type === 'PUT') return 'option'
  if (type === 'FUTURE' || (symbol && symbol.startsWith('/'))) return 'future'
  return 'equity' // default
}

// ── Map asset type to Schwab API format ─────────────────────────────────────

export function mapToSchwabAssetType(assetType) {
  if (assetType === 'equity') return 'EQUITY'
  if (assetType === 'etf') return 'COLLECTIVE_INVESTMENT'
  if (assetType === 'option') return 'OPTION'
  if (assetType === 'future') return 'FUTURE'
  return 'EQUITY'
}

// ── Build normalized symbol ─────────────────────────────────────────────────

function buildNormalizedSymbol(rawSymbol, exp, strike, type, assetType) {
  if (assetType === 'future') return rawSymbol
  if (assetType !== 'option') return rawSymbol

  // Options: build OCC format to match Schwab API output
  // Symbol="SPY", Exp="14 OCT 25", Strike="666", Type="CALL"
  // → "SPY   251014C00666000"

  // Parse expiration: "14 OCT 25" → YYMMDD
  const expMatch = exp.match(/^(\d{1,2})\s+([A-Z]{3})\s+(\d{2})$/)
  if (!expMatch) return rawSymbol // fallback

  const [, expDay, expMonth, expYear] = expMatch
  const monthNum = MONTH_MAP[expMonth]
  if (!monthNum) return rawSymbol

  const yymmdd = `${expYear}${monthNum}${expDay.padStart(2, '0')}`

  // Type: CALL→C, PUT→P
  const cpFlag = type === 'CALL' ? 'C' : 'P'

  // Strike: multiply by 1000, pad to 8 digits
  const strikeNum = Math.round(parseFloat(strike) * 1000)
  const strikePadded = String(strikeNum).padStart(8, '0')

  // Root ticker padded to 6 chars
  const rootPadded = rawSymbol.padEnd(6, ' ')

  return `${rootPadded}${yymmdd}${cpFlag}${strikePadded}`
}

// ── Build schwab_transactions row for insertion ─────────────────────────────

export function buildTransactionRow(exec, userId, accountNumber) {
  const isoDate = exec.tradeDate
  const netAmount = exec.sideRaw === 'BUY'
    ? -(exec.qty * exec.price)
    : (exec.qty * exec.price)

  // Apply multiplier for options
  const multiplier = exec.assetType === 'option' ? 100 : 1

  return {
    user_id: userId,
    account_number: accountNumber,
    activity_id: `csv_${accountNumber}_${isoDate}_${exec.symbol}_${exec.rowIndex}`,
    transaction_type: 'TRADE',
    trade_date: isoDate,
    settlement_date: null,
    net_amount: netAmount * (exec.assetType === 'option' ? multiplier : 1),
    symbol: exec.symbol,
    quantity: exec.qty,
    price: exec.price,
    raw: {
      ...exec.raw,
      transferItems: [
        {
          instrument: {
            assetType: mapToSchwabAssetType(exec.assetType),
            symbol: exec.symbol,
            ...(exec.assetType === 'option' ? { optionPremiumMultiplier: 100 } : {}),
          },
          amount: exec.sideRaw === 'BUY' ? exec.qty : -exec.qty,
          price: exec.price,
          positionEffect: exec.positionEffect,
        },
        {
          instrument: { assetType: 'CURRENCY', symbol: 'CURRENCY_USD' },
          amount: 0,
          cost: 0,
          feeType: 'COMMISSION',
        },
      ],
    },
  }
}
